import { usePartyLicensesQuery } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { getLicenseResumeState } from "Components/Licenses/licenseUtils";
import type { OnboardingStep } from "Components/Onboarding/BusinessEntity/constants";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { RootStateType } from "State/Store";
import { useSelector } from "react-redux";

type OnboardingResumeStep =
  | { isLoading: true }
  | { isLoading: false; initialStep: OnboardingStep };

export const useOnboardingResumeStep = (
  flow: ReadonlyArray<OnboardingStep>,
): OnboardingResumeStep => {
  const currentUser = useLoggedInUser();
  const hasLicensePayment = flow.includes("manualLicensePayment");
  // The persisted user can still be the previous context right after a context-switch
  // reload; its party's licences would 403 with the new token, so wait for fresh user-info.
  const isUserFetching = useSelector(
    (state: RootStateType) => state.auth.isUserFetching,
  );
  const { data, isLoading } = usePartyLicensesQuery(
    hasLicensePayment && !isUserFetching
      ? currentUser?.partyPublicID
      : undefined,
  );

  if (!hasLicensePayment) {
    return { isLoading: false, initialStep: flow[0] };
  }

  if (isLoading || isUserFetching) {
    return { isLoading: true };
  }

  // Fail-open: a failed licences request resumes at step 1 rather than blocking the wizard.
  const { kind } = getLicenseResumeState(data?.items ?? []);

  return {
    isLoading: false,
    initialStep: kind === "none" ? flow[0] : "manualLicensePayment",
  };
};
